import React from "react";
import SEO from "../SEO";
import Section from "../ui/Section";
import { COLORS } from "../../constants";
import ServiceHero from "./ServiceHero";
import ServiceStats from "./ServiceStats";
import ServiceContentSection from "./ServiceContentSection";
import ServiceBenefits from "./ServiceBenefits";
import ServiceBarChart from "./ServiceBarChart";
import ServiceProcessSteps from "./ServiceProcessSteps";
import ServicePricing from "./ServicePricing";
import ServiceEnquiryForm from "./ServiceEnquiryForm";

const DEFAULT_SECTION_ORDER = [
  "hero",
  "stats",
  "sections",
  "benefits",
  "pricing",
  "enquiryForm",
];

const ServicePageLayout = ({ data, sectionOrder = DEFAULT_SECTION_ORDER }) => {
  if (!data || !data.title || !data.introduction) {
    if (import.meta.env.DEV) {
      throw new Error(
        `ServicePageLayout: "title" and "introduction" are required on the service page data. Received: ${JSON.stringify(
          data,
        )}`,
      );
    }
    return null;
  }

  const {
    title,
    introduction,
    hero = {},
    stats = [],
    sections = [],
    benefits,
    pricing,
    enquiryForm,
    seo = {},
  } = data;

  // `stats` may be a plain array (existing shape) or `{ variant, items }` (new).
  const statsConfig = Array.isArray(stats)
    ? { variant: "plain", items: stats }
    : stats || { items: [] };
  const hasStats =
    Array.isArray(statsConfig.items) && statsConfig.items.length > 0;
  const hasSections = Array.isArray(sections) && sections.length > 0;
  const hasBenefits = Boolean(
    benefits && Array.isArray(benefits.items) && benefits.items.length > 0,
  );
  const hasPricing = Boolean(
    pricing && Array.isArray(pricing.plans) && pricing.plans.length > 0,
  );
  const hasEnquiryForm = Boolean(enquiryForm && enquiryForm.enabled !== false);

  // Group consecutive content-section items that share a `group` key into pairs, so
  // they can render side by side in a 2-column grid instead of stacked full-width.
  const sectionRuns = [];
  sections.forEach((section) => {
    const previousRun = sectionRuns[sectionRuns.length - 1];
    const canJoinPreviousRun =
      !section.type &&
      section.group &&
      previousRun &&
      previousRun.group === section.group &&
      previousRun.items.length < 2;
    if (canJoinPreviousRun) {
      previousRun.items.push(section);
    } else {
      sectionRuns.push({
        group: section.type ? null : section.group,
        items: [section],
      });
    }
  });

  const renderers = {
    hero: () => (
      <ServiceHero
        key="hero"
        title={title}
        introduction={introduction}
        supportingText={hero.supportingText}
        eyebrow={hero.eyebrow}
        align={hero.align}
        highlights={hero.highlights}
        image={hero.image}
        cta={hero.cta}
        variant={hero.variant}
      />
    ),
    stats: () =>
      hasStats && (
        <Section key="stats" background="white" padding="large">
          <ServiceStats
            items={statsConfig.items}
            variant={statsConfig.variant}
          />
        </Section>
      ),
    sections: () =>
      hasSections &&
      sectionRuns.map((run, runIndex) => {
        const first = run.items[0];
        const key = first.id || first.title || `section-${runIndex}`;

        if (first.type === "stats") {
          return (
            <Section
              key={key}
              background={first.background || "white"}
              padding="large"
            >
              <ServiceStats items={first.items} variant={first.variant} />
            </Section>
          );
        }

        if (first.type === "benefits") {
          return (
            <Section
              key={key}
              background={first.background || "white"}
              padding="large"
            >
              <ServiceBenefits
                heading={first.heading}
                introduction={first.introduction}
                items={first.items}
                variant={first.variant}
                titleDivider={first.titleDivider}
              />
            </Section>
          );
        }

        if (first.type === "barChart") {
          return (
            <Section
              key={key}
              background={first.background || "white"}
              padding="large"
            >
              <ServiceBarChart heading={first.heading} items={first.items} />
            </Section>
          );
        }

        if (first.type === "process") {
          return (
            <Section
              key={key}
              background={first.background || "white"}
              padding="large"
            >
              <ServiceProcessSteps
                heading={first.heading}
                introduction={first.introduction}
                items={first.items}
              />
            </Section>
          );
        }

        if (run.items.length > 1) {
          return (
            <Section
              key={key}
              background={first.background || "white"}
              padding="large"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
                {run.items.map((section) => (
                  <ServiceContentSection
                    key={section.id || section.title}
                    {...section}
                    variant={section.variant || "compact"}
                  />
                ))}
              </div>
            </Section>
          );
        }

        return (
          <Section
            key={key}
            background={first.background || "white"}
            padding="large"
          >
            <ServiceContentSection {...first} />
          </Section>
        );
      }),
    benefits: () =>
      hasBenefits && (
        <Section
          key="benefits"
          background={benefits.variant === "checklist" ? "white" : "gray"}
          padding="large"
        >
          <ServiceBenefits
            heading={benefits.heading}
            introduction={benefits.introduction}
            items={benefits.items}
            variant={benefits.variant}
          />
        </Section>
      ),
    pricing: () =>
      hasPricing && (
        <Section key="pricing" background="white" padding="large">
          <ServicePricing
            heading={pricing.heading}
            description={pricing.description}
            plans={pricing.plans}
            disclaimer={pricing.disclaimer}
          />
        </Section>
      ),
    enquiryForm: () =>
      hasEnquiryForm && (
        <Section
          key="enquiryForm"
          id={enquiryForm.anchorId}
          className={enquiryForm.anchorId ? "scroll-mt-24" : undefined}
          background="transparent"
          padding="large"
          style={{ backgroundColor: COLORS.secondary }}
        >
          <ServiceEnquiryForm
            serviceName={enquiryForm.serviceName || title}
            webFormId={enquiryForm.webFormId}
            heading={enquiryForm.heading}
            description={enquiryForm.description}
          />
        </Section>
      ),
  };

  return (
    <div>
      <SEO
        title={seo.title || title}
        description={seo.description || introduction}
        keywords={seo.keywords}
        canonical={seo.canonical}
        ogImage={seo.ogImage}
        type={seo.type}
        structuredData={seo.structuredData}
      />
      {sectionOrder.map((key) => renderers[key]?.()).filter(Boolean)}
    </div>
  );
};

export default ServicePageLayout;
